"use client";

import { useTheaterOverlayFrozen } from "@/components/feature/theater-overlay-freeze";
import { type AnimationEvent, type CSSProperties, useEffect, useRef, useState } from "react";

type Star = {
  id: number;
  topPct: number; // 0-100 viewport height percent
  leftPct: number; // 0-100 viewport width percent (start)
  delayMs: number;
  durationMs: number;
  lengthPx: number;
  symbol: string;
};

const PI_SEQUENCE = "π·314159265358979323846264338327950288419716939937510"; // starts with pi symbol then dot then digits

/** Travel keyframe name — only this animation may retire a star (globals.css). */
export const PI_SHOOTING_STAR_MOVE_ANIMATION = "pm-ss-move-left";

/** Default cap — 16 concurrent filter/glow nodes is too heavy on Chromebooks. */
export const PI_SHOOTING_STARS_DEFAULT_MAX_CONCURRENT = 6;

type PiShootingStarsProps = {
  // Delay between shooting stars starting their animation
  minDelayMs?: number; // default ~300ms
  maxDelayMs?: number; // default ~2500ms
  // Travel duration controls speed: shorter = faster
  minDurationMs?: number; // default ~3000ms
  maxDurationMs?: number; // default ~5500ms
  // Limit simultaneous stars
  maxConcurrent?: number; // default 6
};

export function PiShootingStars({
  minDelayMs = 1300,
  maxDelayMs = 4500,
  minDurationMs = 3000,
  maxDurationMs = 7500,
  maxConcurrent = PI_SHOOTING_STARS_DEFAULT_MAX_CONCURRENT,
}: PiShootingStarsProps) {
  const [stars, setStars] = useState<Star[]>([]);
  // false until mount so reduced-motion never flashes a star, and SSR stays empty.
  const [enabled, setEnabled] = useState(false);
  const nextIdxRef = useRef(0);
  const idRef = useRef(0);
  const spawnTimeoutRef = useRef<number | null>(null);
  const pausedRef = useRef(false);
  const overlayFrozen = useTheaterOverlayFrozen();

  const makeStar = (): Star => {
    const id = idRef.current++;
    // random band across screen
    const topPct = Math.random() * 100; // anywhere vertically
    const leftPct = 100 + Math.random() * 20; // start off-screen to the right a bit
    const safeDurMin = Math.max(100, Math.min(minDurationMs, maxDurationMs));
    const safeDurMax = Math.max(minDurationMs, maxDurationMs);
    // Spawn cadence is controlled externally; individual star delay is zero
    const delayMs = 0;
    const durationMs = safeDurMin + Math.floor(Math.random() * (safeDurMax - safeDurMin));
    const lengthPx = 80 + Math.floor(Math.random() * 80); // 80-160px

    // choose next symbol from PI_SEQUENCE
    const symbol = PI_SEQUENCE[nextIdxRef.current % PI_SEQUENCE.length] ?? "";
    nextIdxRef.current = (nextIdxRef.current + 1) % PI_SEQUENCE.length;

    return { id, topPct, leftPct, delayMs, durationMs, lengthPx, symbol };
  };

  // Gate: reduced motion users get a static page (no spawn / no paint).
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setEnabled(!mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  // Pause spawning when the tab is hidden or theater overlay covers this page.
  useEffect(() => {
    const onVisibility = () => {
      pausedRef.current = document.hidden || overlayFrozen;
    };
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [overlayFrozen]);

  // Spawn loop: spawn one immediately, then steadily add stars using min/max delay, up to maxConcurrent
  useEffect(() => {
    if (!enabled || overlayFrozen) {
      setStars([]);
      return;
    }

    let cancelled = false;
    const safeDelayMin = Math.max(0, Math.min(minDelayMs, maxDelayMs));
    const safeDelayMax = Math.max(minDelayMs, maxDelayMs);

    // Immediate first spawn
    setStars((prev) => {
      if (prev.length >= maxConcurrent) return prev;
      return [...prev, makeStar()];
    });

    const scheduleNext = () => {
      if (cancelled) return;
      const wait = safeDelayMin + Math.floor(Math.random() * (safeDelayMax - safeDelayMin + 1));
      spawnTimeoutRef.current = window.setTimeout(() => {
        if (cancelled) return;
        if (!pausedRef.current) {
          setStars((prev) => {
            if (prev.length >= maxConcurrent) return prev;
            return [...prev, makeStar()];
          });
        }
        scheduleNext();
      }, wait);
    };

    scheduleNext();
    return () => {
      cancelled = true;
      if (spawnTimeoutRef.current !== null) {
        clearTimeout(spawnTimeoutRef.current);
        spawnTimeoutRef.current = null;
      }
    };
    // makeStar closes over duration/delay props; deps cover those.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- makeStar is intentionally recreated each effect
  }, [enabled, overlayFrozen, minDelayMs, maxDelayMs, maxConcurrent, minDurationMs, maxDurationMs]);

  // Only the travel animation retires a star — ignore tail/shine and bubbled child events.
  const handleAnimationEnd = (endedId: number, event: AnimationEvent<HTMLSpanElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.animationName !== PI_SHOOTING_STAR_MOVE_ANIMATION) return;
    setStars((prev) => prev.filter((s) => s.id !== endedId));
  };

  if (!enabled) return null;

  return (
    <div
      className="pointer-events-none absolute inset-0"
      style={{ zIndex: 2 }}
      aria-hidden
      data-testid="pi-shooting-stars"
    >
      <div
        className="absolute inset-0"
        style={{
          transform: "rotateZ(-35deg)",
        }}
      >
        {stars.map((star) => (
          <span
            key={star.id}
            className="absolute block h-[2px] rounded-full"
            style={
              {
                top: `${star.topPct}%`,
                left: `${star.leftPct}%`,
                // Tail color — box-shadow is cheaper than filter: drop-shadow on many nodes
                background: "linear-gradient(90deg, rgba(255,255,255,0.95), rgba(180,210,255,0))",
                boxShadow: "0 0 8px rgba(255,255,255,0.85)",
                // animations: constant linear velocity to the left, independent fade via opacity in keyframe
                animationName: `pm-ss-tail, ${PI_SHOOTING_STAR_MOVE_ANIMATION}`,
                animationDuration: `${star.durationMs}ms, ${star.durationMs}ms`,
                animationTimingFunction: "ease-out, linear",
                animationDelay: `${star.delayMs}ms, ${star.delayMs}ms`,
                animationIterationCount: "1, 1",
                animationFillMode: "forwards, forwards",
                // Tail length variable
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore vendor prop type
                "--tail-len": `${star.lengthPx}px`,
              } as CSSProperties
            }
            onAnimationEnd={(event) => handleAnimationEnd(star.id, event)}
          >
            {/* shining tips */}
            <span
              className="absolute block"
              style={{
                top: "-1px",
                left: 0,
                height: "2px",
                background:
                  "linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,1), rgba(255,255,255,0))",
                transform: "translateX(-50%) rotateZ(45deg)",
                borderRadius: 999,
                animation: `pm-ss-shine ${star.durationMs}ms linear ${star.delayMs}ms 1 both`,
              }}
            />
            <span
              className="absolute block"
              style={{
                top: "-1px",
                left: 0,
                height: "2px",
                background:
                  "linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,1), rgba(255,255,255,0))",
                transform: "translateX(-50%) rotateZ(-45deg)",
                borderRadius: 999,
                animation: `pm-ss-shine ${star.durationMs}ms linear ${star.delayMs}ms 1 both`,
              }}
            />

            {/* symbol at tip, counter-rotated to appear upright */}
            <span
              className="absolute block text-white"
              style={{
                left: 0,
                top: "-14px",
                transform: "translateX(-60%) rotateZ(35deg)", // counter the container rotation
                fontFamily: "'Anton', sans-serif",
                fontSize: "18px",
                fontWeight: 700,
                textShadow: "0 0 8px rgba(255,255,255,0.95), 0 0 2px rgba(0,0,0,0.8)",
              }}
            >
              {star.symbol}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
